import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
    ShieldCheck, CheckCircle2, AlertTriangle, XCircle, Loader2, RefreshCw, Clock,
    ClipboardCheck, HelpCircle, Search, User, Flag,
} from "lucide-react";
import { toast } from "@/components/ui/sonner";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";

/**
 * Verification Pending — tabular manual-Checkr operator console.
 *
 * Table columns: Candidate · Passport ID · Type · Status · Submitted · Assigned Admin · Notes · Actions.
 * Rows include synthetic "Consent Pending" rows (talents who haven't consented yet)
 * so ops can nudge them, alongside verified/in_progress/review_required/unsuccessful rows.
 */

const TYPE_LABEL = {
    identity:   "Identity",
    employment: "Employment",
    education:  "Education",
    reference:  "References",
    background: "Background Check",
};

const STATUS_META = {
    consent_pending: { icon: HelpCircle,     label: "Consent pending", tone: "bg-slate-50 text-slate-600 border-slate-200 dark:bg-slate-500/10 dark:text-slate-300 dark:border-slate-500/30" },
    pending_review:  { icon: Clock,          label: "Pending review",  tone: "bg-amber-50 text-amber-800 border-amber-300 dark:bg-amber-500/10 dark:text-amber-300 dark:border-amber-500/40" },
    in_progress:     { icon: Clock,          label: "In progress",     tone: "bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-500/10 dark:text-sky-300 dark:border-sky-500/30" },
    review_required: { icon: AlertTriangle,  label: "Review required", tone: "bg-amber-50 text-amber-800 border-amber-300 dark:bg-amber-500/10 dark:text-amber-300 dark:border-amber-500/40" },
    verified:        { icon: CheckCircle2,   label: "Verified",        tone: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:border-emerald-500/30" },
    unsuccessful:    { icon: XCircle,        label: "Unsuccessful",    tone: "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:border-rose-500/30" },
};

const OUTCOMES_ORDERED = ["in_progress", "verified", "review_required", "unsuccessful"];

function StatusChip({ status }) {
    const meta = STATUS_META[status] || STATUS_META.pending_review;
    const Icon = meta.icon;
    return (
        <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${meta.tone}`}>
            <Icon className="h-3 w-3" /> {meta.label}
        </span>
    );
}

export default function VerificationConsentQueue() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [admins, setAdmins] = useState([]);
    // filters
    const [statusFilter, setStatusFilter] = useState("");
    const [typeFilter, setTypeFilter] = useState("");
    const [assignedFilter, setAssignedFilter] = useState("");
    const [assessmentFilter, setAssessmentFilter] = useState(() => searchParams.get("assessment") || "");
    const [assessmentCounts, setAssessmentCounts] = useState({ review: 0, eligible: 0, escalated: 0, other: 0 });
    const [q, setQ] = useState("");
    const [page, setPage] = useState(1);
    const pageSize = 50;
    // dialog state
    const [target, setTarget] = useState(null);
    const [outcome, setOutcome] = useState("verified");
    const [notes, setNotes] = useState("");
    const [assignee, setAssignee] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
            if (statusFilter) params.set("status", statusFilter);
            if (typeFilter) params.set("type", typeFilter);
            if (assignedFilter) params.set("assigned_admin", assignedFilter);
            if (assessmentFilter) params.set("assessment", assessmentFilter);
            if (q.trim()) params.set("q", q.trim());
            const { data } = await api.get(`/admin/verifications/queue-table?${params.toString()}`);
            setRows(data.results || []);
            setTotal(data.total || 0);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }, [statusFilter, typeFilter, assignedFilter, assessmentFilter, q, page]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        api.get("/admin/verifications/admins-roster")
            .then(({ data }) => setAdmins(data.admins || []))
            .catch(() => setAdmins([]));
        api.get("/admin/verifications/assessment-counts")
            .then(({ data }) => setAssessmentCounts(data.counts || {}))
            .catch(() => {});
    }, []);

    // Keep the assessment filter reflected in the URL so /admin/verification-queue?assessment=review
    // is deep-linkable / bookmarkable. Written whenever the chip changes.
    const setAssessment = useCallback((next) => {
        setAssessmentFilter(next);
        setPage(1);
        const params = new URLSearchParams(searchParams);
        if (next) params.set("assessment", next);
        else params.delete("assessment");
        setSearchParams(params, { replace: true });
    }, [searchParams, setSearchParams]);

    const openDialog = (row) => {
        setTarget(row);
        setOutcome(OUTCOMES_ORDERED.includes(row.status) ? row.status : "verified");
        setNotes(row.notes || "");
        setAssignee(row.assigned_admin || "");
    };

    const submit = async () => {
        if (!target) return;
        setSubmitting(true);
        try {
            await api.post("/admin/verifications/set-outcome", {
                candidate_email: target.candidate_email,
                candidate_name: target.candidate_name || "",
                type: target.type,
                outcome,
                notes: notes,
                assigned_admin: assignee || undefined,
            });
            toast.success(`Recorded ${STATUS_META[outcome].label} for ${TYPE_LABEL[target.type]}.`);
            setTarget(null);
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSubmitting(false);
        }
    };

    const totalPages = Math.max(1, Math.ceil(total / pageSize));

    return (
        <div data-testid="admin-verification-queue">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <p className="overline">Command Center · Simonara Verify™</p>
                    <h1 className="mt-1 font-heading font-medium text-2xl sm:text-3xl tracking-tight text-foreground">
                        Verification Pending
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                        Every candidate × verification type on the platform. Run each row through
                        Checkr in your provider dashboard, then click Set outcome. Employers see a single
                        &quot;Verified&quot; badge — never these internal states.
                    </p>
                </div>
                <button
                    onClick={load}
                    disabled={loading}
                    data-testid="vq-refresh"
                    className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted disabled:opacity-60"
                >
                    <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
                </button>
            </div>

            {/* Filters */}
            <div className="mt-5 flex flex-wrap items-center gap-2" data-testid="vq-assessment-chips">
                <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mr-1">Checkr Assess:</span>
                {[
                    { key: "", label: "All", icon: null, count: null },
                    { key: "review", label: "Review-flagged", icon: Flag, count: assessmentCounts.review },
                    { key: "eligible", label: "Eligible", icon: CheckCircle2, count: assessmentCounts.eligible },
                    { key: "escalated", label: "Escalated", icon: AlertTriangle, count: assessmentCounts.escalated },
                ].map((chip) => {
                    const active = (assessmentFilter || "") === chip.key;
                    const Icon = chip.icon;
                    const tone = chip.key === "review"
                        ? (active ? "bg-amber-500 text-white border-amber-500" : "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/40 hover:bg-amber-500/20")
                        : (active ? "bg-brand-navy text-white border-brand-navy dark:bg-slate-700 dark:border-slate-700" : "bg-muted/40 text-foreground border-border hover:bg-muted");
                    return (
                        <button
                            key={chip.key || "all"}
                            type="button"
                            onClick={() => setAssessment(chip.key)}
                            data-testid={`vq-chip-assessment-${chip.key || "all"}`}
                            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${tone}`}
                        >
                            {Icon ? <Icon size={11} /> : null}
                            {chip.label}
                            {chip.count != null && chip.count > 0 && (
                                <span className={`ml-0.5 inline-flex items-center rounded-full px-1.5 text-[10px] font-bold ${active ? "bg-white/20" : "bg-black/10 dark:bg-white/10"}`}>
                                    {chip.count}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>

            <div className="mt-3 grid grid-cols-1 sm:grid-cols-4 gap-2">
                <div className="relative sm:col-span-2">
                    <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                        value={q}
                        onChange={(e) => { setQ(e.target.value); setPage(1); }}
                        placeholder="Search name, email, passport id"
                        data-testid="vq-filter-q"
                        className="pl-9"
                    />
                </div>
                <Select value={statusFilter || "all"} onValueChange={(v) => { setStatusFilter(v === "all" ? "" : v); setPage(1); }}>
                    <SelectTrigger data-testid="vq-filter-status"><SelectValue placeholder="Any status" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Any status</SelectItem>
                        {Object.entries(STATUS_META).map(([k, m]) => (
                            <SelectItem key={k} value={k}>{m.label}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Select value={typeFilter || "all"} onValueChange={(v) => { setTypeFilter(v === "all" ? "" : v); setPage(1); }}>
                    <SelectTrigger data-testid="vq-filter-type"><SelectValue placeholder="Any type" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Any type</SelectItem>
                        {Object.entries(TYPE_LABEL).map(([k, l]) => (
                            <SelectItem key={k} value={k}>{l}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {/* Table */}
            <div className="mt-4 rounded-2xl border border-border bg-card overflow-hidden">
                {loading ? (
                    <div className="py-16 flex justify-center"><Loader2 size={18} className="animate-spin text-emerald-500" /></div>
                ) : rows.length === 0 ? (
                    <div data-testid="vq-empty" className="p-12 text-center">
                        <ShieldCheck className="mx-auto h-8 w-8 text-muted-foreground" />
                        <h3 className="mt-3 text-base font-semibold text-foreground">Nothing matches these filters.</h3>
                        <p className="mt-1 text-sm text-muted-foreground">Try clearing the search / filters above.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-[11px] uppercase tracking-wider text-muted-foreground border-b border-border bg-muted/30">
                                    <th className="px-4 py-3 font-semibold">Candidate</th>
                                    <th className="px-4 py-3 font-semibold">Passport ID</th>
                                    <th className="px-4 py-3 font-semibold">Type</th>
                                    <th className="px-4 py-3 font-semibold">Status</th>
                                    <th className="px-4 py-3 font-semibold">Submitted</th>
                                    <th className="px-4 py-3 font-semibold">Assigned admin</th>
                                    <th className="px-4 py-3 font-semibold">Notes</th>
                                    <th className="px-4 py-3 font-semibold text-right">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => {
                                    const rowKey = `${r.candidate_email}::${r.type}`;
                                    return (
                                        <tr
                                            key={rowKey}
                                            data-testid={`vq-row-${r.candidate_email}-${r.type}`}
                                            className="border-b border-border/50 last:border-b-0 hover:bg-muted/30"
                                        >
                                            <td className="px-4 py-3">
                                                <div className="font-medium text-foreground">{r.candidate_name || <em className="text-muted-foreground">—</em>}</div>
                                                <div className="text-xs text-muted-foreground">{r.candidate_email}</div>
                                            </td>
                                            <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{(r.passport_id || "").slice(0, 12) || "—"}</td>
                                            <td className="px-4 py-3">{TYPE_LABEL[r.type] || r.type}</td>
                                            <td className="px-4 py-3">
                                                <StatusChip status={r.status} />
                                                {r.checkr_assessment_raw === "review" && (
                                                    <span
                                                        data-testid={`vq-row-review-badge-${r.candidate_email}-${r.type}`}
                                                        className="ml-2 inline-flex items-center gap-1 rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-500/40 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider"
                                                        title="Checkr Assess flagged this candidate for human review even though the report itself came back clear."
                                                    >
                                                        <Flag size={9} /> Review
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-xs text-muted-foreground">{r.submitted_at ? new Date(r.submitted_at).toLocaleDateString() : "—"}</td>
                                            <td className="px-4 py-3 text-xs">
                                                {r.assigned_admin ? (
                                                    <span className="inline-flex items-center gap-1 text-foreground">
                                                        <User className="h-3 w-3" /> {r.assigned_admin}
                                                    </span>
                                                ) : <span className="text-muted-foreground">Unassigned</span>}
                                            </td>
                                            <td className="px-4 py-3 text-xs text-muted-foreground truncate max-w-[16ch]" title={r.notes || ""}>{r.notes || "—"}</td>
                                            <td className="px-4 py-3 text-right">
                                                {r.status === "consent_pending" ? (
                                                    <span className="text-[11px] text-muted-foreground italic">Waiting on talent consent</span>
                                                ) : (
                                                    <button
                                                        onClick={() => openDialog(r)}
                                                        data-testid={`vq-action-${r.candidate_email}-${r.type}`}
                                                        className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-50 dark:bg-emerald-500/10 dark:border-emerald-500/40 px-2.5 py-1 text-[11px] font-semibold text-emerald-800 dark:text-emerald-300 hover:bg-emerald-100"
                                                    >
                                                        <ClipboardCheck className="h-3 w-3" /> Set outcome
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Pagination */}
            {total > pageSize && (
                <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
                    <span>Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}</span>
                    <div className="flex gap-1">
                        <button
                            disabled={page <= 1}
                            onClick={() => setPage(page - 1)}
                            className="btn-outline px-3 py-1 rounded-full disabled:opacity-40"
                        >← Prev</button>
                        <button
                            disabled={page >= totalPages}
                            onClick={() => setPage(page + 1)}
                            className="btn-outline px-3 py-1 rounded-full disabled:opacity-40"
                        >Next →</button>
                    </div>
                </div>
            )}

            {/* Outcome + assignment dialog */}
            <Dialog open={!!target} onOpenChange={(o) => !o && setTarget(null)}>
                <DialogContent data-testid="vq-outcome-dialog">
                    <DialogHeader>
                        <DialogTitle>Set verification outcome</DialogTitle>
                        <DialogDescription>
                            {target && (
                                <>
                                    {TYPE_LABEL[target.type] || target.type} check for{" "}
                                    <span className="font-semibold text-foreground">{target.candidate_email}</span>.
                                    Talents see a friendly label; employers see a single &quot;Verified&quot; badge.
                                </>
                            )}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="py-2 space-y-4">
                        <div>
                            <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">Outcome</div>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                {OUTCOMES_ORDERED.map((k) => {
                                    const meta = STATUS_META[k];
                                    const Icon = meta.icon;
                                    return (
                                        <button
                                            key={k}
                                            type="button"
                                            onClick={() => setOutcome(k)}
                                            data-testid={`vq-outcome-${k}`}
                                            className={`flex flex-col items-center gap-1 rounded-xl border px-3 py-3 text-xs font-semibold transition-all ${
                                                outcome === k
                                                    ? `${meta.tone} ring-2 ring-offset-1 ring-emerald-400/40`
                                                    : "border-border text-muted-foreground hover:bg-muted"
                                            }`}
                                        >
                                            <Icon className="h-4 w-4" />
                                            {meta.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        <div>
                            <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">Assign to admin</div>
                            <Select value={assignee || "unassigned"} onValueChange={(v) => setAssignee(v === "unassigned" ? "" : v)}>
                                <SelectTrigger data-testid="vq-assign-picker"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="unassigned">Unassigned (I&apos;ll pick this up)</SelectItem>
                                    {admins.map((a) => (
                                        <SelectItem key={a.email} value={a.email}>
                                            {a.email} · <span className="text-muted-foreground">{a.admin_role}</span>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div>
                            <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">Internal note (audited, never shown to talent)</div>
                            <Textarea
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                data-testid="vq-outcome-notes"
                                className="min-h-[70px]"
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <button
                            onClick={() => setTarget(null)}
                            className="btn-outline text-sm px-4 py-2"
                            data-testid="vq-outcome-cancel"
                        >Cancel</button>
                        <button
                            onClick={submit}
                            disabled={submitting}
                            className="btn-green text-sm px-4 py-2 inline-flex items-center gap-1 disabled:opacity-60"
                            data-testid="vq-outcome-confirm"
                        >
                            {submitting ? <Loader2 size={13} className="animate-spin" /> : null} Save outcome
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
